import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./moreResourcesV2.html";
import { responsiveBinding } from '~/components/wds/common/responsiveHelper';
// The grid collapses from two columns to one at this width. MUST stay in sync
// with the `@media (max-width: ...)` breakpoint in moreResourcesV2.css: the JS
// order and the CSS layout have to flip at the exact same pixel or the DOM
// (assistive) reading order diverges from the visual order (WCAG 1.3.2).
const SINGLE_COLUMN_MAX_WIDTH = '1023px';

/**
 * Section importance to users, most important first (product decision,
 * v2-only). Any type not listed (e.g. Customer Stories, Promotion, or a future
 * content type) ranks after these, in the backend's original order, so no real
 * content is dropped. Empty categories never reach here — the backend only
 * sends types that have resources.
 */
const SECTION_ORDER = ['Customization Guide',
// Guides
'Data Sheet', 'Webinar', 'Customer Testimonial',
// Testimonials
'Whitepaper', 'Case Study'];

// Precomputed once at module load: type -> importance rank. Avoids an
// Array.indexOf scan inside the sort comparator on every render.
const RANK_BY_TYPE = new Map(SECTION_ORDER.map((type, index) => [type, index]));

/** One rendered column of section cards, stacked top-to-bottom. */

class MoreResourcesV2 extends LightningElement {
  constructor(...args) {
    super(...args);
    // The grid is two columns on desktop and collapses to one at
    // <=SINGLE_COLUMN_MAX_WIDTH. The number of columns (and thus how the cards
    // are distributed) flips with the viewport — see `columns`.
    this.isSingleColumn = false;
    this.disposeResponsiveBinding = null;
    // Caches the importance-sorted sections so the sort only re-runs when the
    // `resources` input actually changes, not on every render / viewport flip.
    this.sortedCache = null;
  }
  set resources(value) {
    this._resources = value;
    // Invalidate the importance-sort cache; `columns` recomputes lazily.
    this.sortedCache = null;
  }
  get resources() {
    return this._resources;
  }
  connectedCallback() {
    this.disposeResponsiveBinding = responsiveBinding(SINGLE_COLUMN_MAX_WIDTH, matches => {
      this.isSingleColumn = matches;
    });
  }
  disconnectedCallback() {
    this.disposeResponsiveBinding?.();
    this.disposeResponsiveBinding = null;
  }

  /**
   * Sections ordered by importance (SECTION_ORDER), ties broken on the
   * backend's original order so equal-rank / unranked sections stay put.
   * Memoized against the `resources` input via `sortedCache`.
   */
  get byImportance() {
    if (this.sortedCache) {
      return this.sortedCache;
    }
    const rank = type => RANK_BY_TYPE.get(type) ?? SECTION_ORDER.length;
    this.sortedCache = [...(this.resources ?? [])].map((section, index) => ({
      section,
      index
    })).sort((a, b) => rank(a.section.type) - rank(b.section.type) || a.index - b.index).map(({
      section
    }) => section);
    return this.sortedCache;
  }

  /**
   * The section cards distributed into explicit columns, rendered as
   * independent vertical stacks (moreResourcesV2.html/.css) so the masonry look
   * is preserved without the height-balancing of CSS `column-count`, which
   * could pile every card into the left column when only a couple of unequal
   * sections exist. Each column stacks its cards top-to-bottom.
   *
   * Single column (tablet/mobile): one column in plain importance order.
   *
   * Two columns (desktop): sections alternate left, right, left, ... by
   * importance so the grid reads left-to-right / top-to-bottom (row i pairs
   * byImportance[2i] on the left with byImportance[2i+1] on the right). With
   * fewer sections than columns the leftmost columns simply fill first, so two
   * sections land one-per-column left to right instead of stacking in one.
   */
  get columns() {
    const byImportance = this.byImportance;
    if (this.isSingleColumn) {
      return [{
        key: 'column-0',
        sections: byImportance
      }];
    }
    const left = byImportance.filter((_, index) => index % 2 === 0);
    const right = byImportance.filter((_, index) => index % 2 === 1);
    return [{
      key: 'column-0',
      sections: left
    }, {
      key: 'column-1',
      sections: right
    }];
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(MoreResourcesV2, {
  publicProps: {
    resources: {
      config: 3
    }
  },
  track: {
    isSingleColumn: 1
  },
  fields: ["disposeResponsiveBinding", "sortedCache"]
});
const __lwc_component_class_internal = _registerComponent(MoreResourcesV2, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;